/**
 * Settings Reducer
 * NgRx reducer for settings state management
 */
import { createReducer, on } from '@ngrx/store';
import {
  UserProfile,
  Organization,
  TeamMember,
  NotificationPreferences,
  TeamMembersQuery
} from '../models/settings.models';
import * as SettingsActions from './settings.actions';

export interface SettingsState {
  userProfile: UserProfile | null;
  organization: Organization | null;
  teamMembers: TeamMember[];
  teamMembersQuery: TeamMembersQuery;
  teamMembersTotal: number;
  notificationPreferences: NotificationPreferences | null;
  loading: boolean;
  teamMembersLoading: boolean;
  saving: boolean;
  uploading: boolean;
  inviting: boolean;
  error: string | null;
}

export const initialState: SettingsState = {
  userProfile: null,
  organization: null,
  teamMembers: [],
  teamMembersQuery: {
    page: 1,
    pageSize: 10,
    search: '',
    role: '',
    status: '',
    sort: '-last_active'
  },
  teamMembersTotal: 0,
  notificationPreferences: null,
  loading: false,
  teamMembersLoading: false,
  saving: false,
  uploading: false,
  inviting: false,
  error: null
};

export const settingsReducer = createReducer(
  initialState,

  // Load User Profile
  on(SettingsActions.loadUserProfile, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(SettingsActions.loadUserProfileSuccess, (state, { profile }) => ({
    ...state,
    userProfile: profile,
    loading: false
  })),
  on(SettingsActions.loadUserProfileFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Update User Profile
  on(SettingsActions.updateUserProfile, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(SettingsActions.updateUserProfileSuccess, (state, { profile }) => ({
    ...state,
    userProfile: profile,
    saving: false
  })),
  on(SettingsActions.updateUserProfileFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Upload Avatar
  on(SettingsActions.uploadAvatar, (state) => ({
    ...state,
    uploading: true,
    error: null
  })),
  on(SettingsActions.uploadAvatarSuccess, (state, { avatarUrl }) => ({
    ...state,
    userProfile: state.userProfile
      ? {
          ...state.userProfile,
          ...(avatarUrl ? { avatar_url: avatarUrl } : {})
        }
      : null,
    uploading: false
  })),
  on(SettingsActions.uploadAvatarFailure, (state, { error }) => ({
    ...state,
    uploading: false,
    error
  })),

  // Password reset email
  on(SettingsActions.requestPasswordReset, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(SettingsActions.requestPasswordResetSuccess, (state) => ({
    ...state,
    saving: false
  })),
  on(SettingsActions.requestPasswordResetFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Enable 2FA
  on(SettingsActions.enable2FA, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(SettingsActions.enable2FASuccess, (state) => ({
    ...state,
    userProfile: state.userProfile
      ? { ...state.userProfile, has_2fa_enabled: true, two_factor_enabled: true }
      : null,
    saving: false
  })),
  on(SettingsActions.enable2FAFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Disable 2FA
  on(SettingsActions.disable2FA, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(SettingsActions.disable2FASuccess, (state) => ({
    ...state,
    userProfile: state.userProfile
      ? { ...state.userProfile, has_2fa_enabled: false, two_factor_enabled: false }
      : null,
    saving: false
  })),
  on(SettingsActions.disable2FAFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Load Organization
  on(SettingsActions.loadOrganization, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(SettingsActions.loadOrganizationSuccess, (state, { organization }) => ({
    ...state,
    organization,
    loading: false
  })),
  on(SettingsActions.loadOrganizationFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Update Organization
  on(SettingsActions.updateOrganization, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(SettingsActions.updateOrganizationSuccess, (state, { organization }) => ({
    ...state,
    organization,
    saving: false
  })),
  on(SettingsActions.updateOrganizationFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Upload Logo
  on(SettingsActions.uploadLogo, (state) => ({
    ...state,
    uploading: true,
    error: null
  })),
  on(SettingsActions.uploadLogoSuccess, (state, { logoUrl }) => ({
    ...state,
    organization: state.organization ? { ...state.organization, logo_url: logoUrl } : null,
    uploading: false
  })),
  on(SettingsActions.uploadLogoFailure, (state, { error }) => ({
    ...state,
    uploading: false,
    error
  })),

  // Load Team Members
  on(SettingsActions.loadTeamMembers, (state, { query }) => ({
    ...state,
    teamMembersQuery: query ? { ...state.teamMembersQuery, ...query } : state.teamMembersQuery,
    teamMembersLoading: true,
    error: null
  })),
  on(SettingsActions.setTeamMembersFilters, (state, { search, role, status, sort }) => ({
    ...state,
    teamMembersQuery: {
      ...state.teamMembersQuery,
      page: 1,
      ...(search !== undefined ? { search } : {}),
      ...(role !== undefined ? { role } : {}),
      ...(status !== undefined ? { status } : {}),
      ...(sort !== undefined ? { sort } : {})
    }
  })),
  on(SettingsActions.setTeamMembersPage, (state, { page, pageSize }) => ({
    ...state,
    teamMembersQuery: {
      ...state.teamMembersQuery,
      page,
      ...(pageSize !== undefined ? { pageSize } : {})
    }
  })),
  on(SettingsActions.loadTeamMembersSuccess, (state, { members, total, page, pageSize }) => ({
    ...state,
    teamMembers: members,
    teamMembersTotal: total,
    teamMembersQuery: {
      ...state.teamMembersQuery,
      page,
      pageSize
    },
    teamMembersLoading: false
  })),
  on(SettingsActions.loadTeamMembersFailure, (state, { error }) => ({
    ...state,
    teamMembersLoading: false,
    error
  })),

  // Invite Team Member
  on(SettingsActions.inviteTeamMember, (state) => ({
    ...state,
    inviting: true,
    error: null
  })),
  on(SettingsActions.inviteTeamMemberSuccess, (state, { member }) => ({
    ...state,
    teamMembers: member ? [...state.teamMembers, member] : state.teamMembers,
    inviting: false
  })),
  on(SettingsActions.inviteTeamMemberFailure, (state, { error }) => ({
    ...state,
    inviting: false,
    error
  })),

  // Update Team Member Role
  on(SettingsActions.updateTeamMemberRole, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(SettingsActions.updateTeamMemberRoleSuccess, (state, { member }) => {
    if (!member?._id) {
      return { ...state, saving: false };
    }
    return {
      ...state,
      teamMembers: state.teamMembers.map(m => m._id === member._id ? { ...m, ...member } : m),
      saving: false
    };
  }),
  on(SettingsActions.updateTeamMemberRoleFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Remove Team Member
  on(SettingsActions.removeTeamMember, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(SettingsActions.removeTeamMemberSuccess, (state, { memberId }) => ({
    ...state,
    teamMembers: state.teamMembers.filter(m => m._id !== memberId),
    saving: false
  })),
  on(SettingsActions.removeTeamMemberFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Resend Invitation
  on(SettingsActions.resendInvitation, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(SettingsActions.resendInvitationSuccess, (state) => ({
    ...state,
    saving: false
  })),
  on(SettingsActions.resendInvitationFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Load Notification Preferences
  on(SettingsActions.loadNotificationPreferences, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(SettingsActions.loadNotificationPreferencesSuccess, (state, { preferences }) => ({
    ...state,
    notificationPreferences: preferences,
    loading: false
  })),
  on(SettingsActions.loadNotificationPreferencesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Update Notification Preferences
  on(SettingsActions.updateNotificationPreferences, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(SettingsActions.updateNotificationPreferencesSuccess, (state, { preferences }) => ({
    ...state,
    notificationPreferences: preferences,
    saving: false
  })),
  on(SettingsActions.updateNotificationPreferencesFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Connect Slack
  on(SettingsActions.connectSlack, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(SettingsActions.connectSlackSuccess, (state, { preferences }) => ({
    ...state,
    notificationPreferences: preferences,
    saving: false
  })),
  on(SettingsActions.connectSlackFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  // Disconnect Slack
  on(SettingsActions.disconnectSlack, (state) => ({
    ...state,
    saving: true,
    error: null
  })),
  on(SettingsActions.disconnectSlackSuccess, (state, { preferences }) => ({
    ...state,
    notificationPreferences: preferences,
    saving: false
  })),
  on(SettingsActions.disconnectSlackFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  }))
);

